/**
 * DAAM Leads — Public campaign landing page.
 *
 * Rendered at /l/:businessSlug/:campaignSlug. Mobile-first, RTL,
 * minimal shell: hero media, headline, offer copy, CTAs. Each CTA
 * hits an opaque internal path /api/leads/rc/{business}/{campaign}/{action}
 * that resolves the real destination server-side (so the browser
 * never sees phone numbers or external URLs).
 *
 * Attribution:
 *   • ?s=…  (source: instagram | tiktok | snapchat | x | direct | other)
 *   • ?c=…  (campaign code — defaults to the campaign's own code)
 * Both are passed through to the redirect router unchanged.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  MessageCircle, Phone, ShoppingBag, Globe2, Loader2,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API_BASE, withCredentials: false });

const BTN_META = {
  whatsapp: { label: "تواصل عبر واتساب", icon: MessageCircle, accent: "bg-[#25D366] hover:bg-[#1EBE5D]" },
  call:     { label: "اتصال مباشر",      icon: Phone,         accent: "bg-neutral-900 hover:bg-neutral-800" },
  order:    { label: "اطلب الآن",        icon: ShoppingBag,   accent: "bg-amber-600 hover:bg-amber-700" },
  website:  { label: "زيارة الموقع",     icon: Globe2,        accent: "bg-neutral-900 hover:bg-neutral-800" },
};

export default function PublicCampaignPage() {
  const { businessSlug, campaignSlug } = useParams();
  const [sp] = useSearchParams();
  const [state, setState] = useState({ status: "loading" });

  const source   = sp.get("s") || "";
  const campaign = sp.get("c") || "";

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    publicApi.get(`/leads/public/${encodeURIComponent(businessSlug)}/${encodeURIComponent(campaignSlug)}`)
      .then((r) => {
        if (cancelled) return;
        setState({ status: "ok", data: r.data });
        // Fire-and-forget page-view beacon
        publicApi.post(
          `/leads/track/view/${encodeURIComponent(businessSlug)}/${encodeURIComponent(campaignSlug)}`,
          { source: source || null, campaign: campaign || null },
        ).catch(() => {});
      })
      .catch((e) => {
        if (cancelled) return;
        const s = e?.response?.status;
        setState({ status: (s === 404 || s === 410) ? "missing" : "error" });
      });
    return () => { cancelled = true; };
  }, [businessSlug, campaignSlug, source, campaign]);

  if (state.status === "loading") {
    return <FullScreen><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></FullScreen>;
  }
  if (state.status === "missing") {
    return (
      <FullScreen>
        <div dir="rtl" data-testid="campaign-public-unavailable" className="text-center max-w-xs">
          <h1 className="text-lg font-semibold text-neutral-900">هذه الحملة غير متاحة حاليًا</h1>
          <p className="mt-2 text-sm text-neutral-500 leading-6">تأكد من الرابط أو حاول لاحقًا.</p>
          <Link to="/" className="mt-6 inline-block text-sm text-neutral-700 underline underline-offset-4">
            العودة إلى DAAM
          </Link>
        </div>
      </FullScreen>
    );
  }
  if (state.status === "error") {
    return (
      <FullScreen>
        <div dir="rtl" className="text-center max-w-xs">
          <h1 className="text-lg font-semibold text-neutral-900">تعذّر تحميل الصفحة</h1>
          <p className="mt-2 text-sm text-neutral-500 leading-6">حاول مجددًا بعد قليل.</p>
        </div>
      </FullScreen>
    );
  }

  return <CampaignView data={state.data} source={source} campaign={campaign} />;
}

function CampaignView({ data, source, campaign }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const images = (data.media || []).filter((m) => m.kind === "image");
  const video  = (data.media || []).find((m) => m.kind === "video");

  const ctaOrder = ["whatsapp", "call", "order", "website"];
  const avail = ctaOrder.filter((k) => data.buttons[k]?.enabled);
  const primaryKey = data.primary_cta && avail.includes(data.primary_cta) ? data.primary_cta : null;
  const secondary  = avail.filter((k) => k !== primaryKey);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  // Click-tracking nonce — rotated on every pointer/touch/context event
  // (see rotateHref below). Prevents browser memory-cache from replaying
  // a previous redirect for repeat clicks on the same CTA. Combined with
  // the server's Cache-Control: no-store + 303 See Other, this gives us
  // "exactly one event per click" semantics in Chrome / Safari / Firefox.
  const click = (path) => {
    if (!path) return "#";
    const qs = new URLSearchParams();
    if (source)   qs.set("s", source);
    if (campaign) qs.set("c", campaign);
    qs.set("_c", `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`);
    return `${backend}${path}?${qs.toString()}`;
  };

  // Right before the browser navigates, mutate the anchor's href with
  // a fresh nonce so each click is a URL the browser has never seen.
  const rotateHref = (e) => {
    try {
      const a = e.currentTarget;
      const url = new URL(a.href, window.location.origin);
      url.searchParams.set("_c", `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`);
      a.href = url.toString();
    } catch { /* no-op — fallback to the href already on the element */ }
  };

  return (
    <div dir="rtl"
         data-testid="campaign-public-page"
         className="min-h-screen bg-neutral-50">
      <div className="max-w-md mx-auto bg-white sm:my-6 sm:rounded-3xl sm:ring-1 sm:ring-neutral-100 sm:shadow-sm overflow-hidden">
        {/* Hero media */}
        <div className="relative bg-neutral-100 aspect-[4/3] overflow-hidden">
          {video ? (
            <video src={video.url} controls playsInline
                   data-testid="campaign-public-video"
                   className="w-full h-full object-cover bg-black" />
          ) : images.length > 0 ? (
            <img src={images[activeIdx]?.url}
                 alt=""
                 data-testid="campaign-public-hero-img"
                 className="w-full h-full object-cover transition-opacity"
                 loading="eager" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">
              {data.business_name}
            </div>
          )}
          {/* small business badge on top-right (RTL start) */}
          {data.logo_url && (
            <div className="absolute top-3 start-3 h-10 w-10 rounded-full overflow-hidden bg-white ring-1 ring-black/10 shadow">
              <img src={data.logo_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
        </div>

        {/* Thumbnails when there are multiple images and no video */}
        {!video && images.length > 1 && (
          <div className="flex gap-1.5 p-2 overflow-x-auto bg-neutral-50">
            {images.map((m, i) => (
              <button key={m.id}
                      type="button"
                      onClick={() => setActiveIdx(i)}
                      data-testid={`campaign-public-thumb-${i}`}
                      className={`shrink-0 h-14 w-14 rounded-md ring-1 overflow-hidden ${
                        i === activeIdx ? "ring-amber-500" : "ring-neutral-200"
                      }`}>
                <img src={m.url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Copy */}
        <div className="p-5 sm:p-6">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">
            {data.business_name}
          </div>
          <h1 data-testid="campaign-public-headline"
              className="mt-1 text-xl sm:text-2xl font-semibold leading-snug text-neutral-900">
            {data.headline}
          </h1>
          {data.offer_description && (
            <p data-testid="campaign-public-desc"
               className="mt-3 text-sm sm:text-base text-neutral-700 leading-7 whitespace-pre-wrap">
              {data.offer_description}
            </p>
          )}

          {/* CTAs */}
          <div className="mt-6 space-y-2.5" data-testid="campaign-public-ctas">
            {avail.length === 0 && (
              <p className="text-center text-xs text-neutral-400">لا توجد أزرار مفعّلة.</p>
            )}
            {primaryKey && (
              <CtaButton k={primaryKey} big path={data.buttons[primaryKey].path} click={click} rotateHref={rotateHref} />
            )}
            {secondary.map((k) => (
              <CtaButton key={k} k={k} path={data.buttons[k].path} click={click} rotateHref={rotateHref} />
            ))}
          </div>
        </div>
      </div>

      <div className="pb-8 text-center text-[11px] text-neutral-400">
        Powered by <span className="font-semibold text-neutral-500">DAAM</span>
      </div>
    </div>
  );
}

function CtaButton({ k, big, path, click, rotateHref }) {
  const m = BTN_META[k];
  const Icon = m.icon;
  return (
    <a href={click(path)}
       data-testid={`campaign-public-cta-${k}`}
       rel="nofollow noopener noreferrer"
       onMouseDown={rotateHref}
       onTouchStart={rotateHref}
       onContextMenu={rotateHref}
       target={k === "website" || k === "order" ? "_blank" : undefined}
       className={`w-full rounded-2xl text-white flex items-center justify-center gap-2 transition-colors ${m.accent} ${big ? "px-4 py-4 text-base font-semibold" : "px-4 py-3 text-sm font-medium"}`}>
      <Icon className={big ? "h-5 w-5" : "h-4 w-4"} />
      <span>{m.label}</span>
    </a>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-neutral-50">
      {children}
    </div>
  );
}
